import type { JsonSchemaType } from '@gmn-portal/backend';
import { Building2, CircleDot, Database, Edit3, Ship } from 'lucide-react';
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { Link } from 'react-router-dom';
import type { z } from 'zod';
import { useIntl } from '@/core/i18n/use-intl';
import { MultiSearchCombobox, SearchCombobox } from '@/shared/components/inputs';
import { DetailSectionHeader, EntityReferenceValue } from '@/shared/components/layout';
import { MetadataAutoForm } from '@/shared/components/metadata/metadata-auto-form';
import { MetadataReadView } from '@/shared/components/metadata/metadata-read-view';
import {
  Badge,
  Button,
  DatePicker,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Textarea,
} from '@/shared/components/ui';
import { pickZodObjectFields } from '@/shared/utils';
import { getFieldPermission } from '@/shared/utils/metadata';
import type { ContractFormData } from '../types/contract-form';

type Mode = 'read' | 'edit';

export type ContractMetadataSection = { id: string; label: string; fieldNames: string[] };

const getOrderedFieldNames = (metadataSchema: JsonSchemaType) => {
  const fromOrder = metadataSchema.propertyOrder?.length ? metadataSchema.propertyOrder : [];
  return fromOrder.length > 0 ? fromOrder : Object.keys(metadataSchema.properties || {});
};

const getKnownGroupIds = (metadataSchema: JsonSchemaType) =>
  new Set((metadataSchema.groups ?? []).map((g) => g.id));

const getFieldsForGroup = (metadataSchema: JsonSchemaType, groupId: string) => {
  const ordered = getOrderedFieldNames(metadataSchema);
  return ordered.filter((name) => metadataSchema.properties?.[name]?.groupId === groupId);
};

const getUngroupedFields = (metadataSchema: JsonSchemaType) => {
  const ordered = getOrderedFieldNames(metadataSchema);
  const knownGroupIds = getKnownGroupIds(metadataSchema);
  return ordered.filter((name) => {
    const field = metadataSchema.properties?.[name];
    const fieldGroupId = field?.groupId;
    if (!fieldGroupId) return true;
    return !knownGroupIds.has(fieldGroupId);
  });
};

export function buildContractMetadataSections({
  schema,
  locale,
  ungroupedLabel,
  groupIdPrefix,
  ungroupedId,
  groupKey,
}: {
  schema: JsonSchemaType | undefined;
  locale: string;
  ungroupedLabel: string;
  groupIdPrefix: string;
  ungroupedId: string;
  groupKey?: string | null;
}): ContractMetadataSection[] {
  if (!schema || Object.keys(schema.properties || {}).length === 0) return [];

  const groups = [...(schema.groups ?? [])].sort((a, b) => a.order - b.order);
  const filterReadableFields = (fieldNames: string[]) =>
    fieldNames.filter((name) => {
      const field = schema.properties?.[name];
      if (!field) return false;
      return getFieldPermission(field, groupKey ?? null).read;
    });
  const sections = groups
    .map((group) => ({
      id: `${groupIdPrefix}-${group.id}`,
      label: locale === 'en-US' && group.labelEn ? group.labelEn : group.labelDe,
      fieldNames: filterReadableFields(getFieldsForGroup(schema, group.id)),
    }))
    .filter((s) => s.fieldNames.length > 0);

  const ungrouped = filterReadableFields(getUngroupedFields(schema));
  if (ungrouped.length > 0) {
    sections.push({
      id: ungroupedId,
      label: ungroupedLabel,
      fieldNames: ungrouped,
    });
  }

  return sections;
}

const readCard = (label: string, value: ReactNode) => (
  <div className="rounded-xl border border-border bg-card p-4">
    <div className="text-xs font-medium text-muted-foreground mb-1">{label}</div>
    <div className="text-sm font-medium">{value}</div>
  </div>
);

const toValidDate = (value: string): Date | undefined => {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

type ContractMasterDataSectionProps = {
  id?: string;
  mode: Mode;
  form: UseFormReturn<ContractFormData, any, ContractFormData>;
  canEditContracts: boolean;
  canReadObjects: boolean;
  disableCustomerSelection?: boolean;
  customerName?: string;
  customerHref?: string | null;
  aggregatedObjectIds: string[];
  aggregatedObjects?: Array<{ id: string; name: string; href?: string | null }>;
  isLoadingObjects?: boolean;
};

export function ContractMasterDataSection({
  id,
  mode,
  form,
  canEditContracts,
  canReadObjects,
  disableCustomerSelection,
  customerName,
  customerHref,
  aggregatedObjectIds,
  aggregatedObjects,
  isLoadingObjects,
}: ContractMasterDataSectionProps) {
  const { t } = useIntl();

  const title = form.watch('title') || '';
  const orderNumber = form.watch('orderNumber') || '';
  const note = form.watch('note') || '';
  const weaOssCount = form.watch('weaOssCount');
  const status = form.watch('status') || 'open';
  const startDate = form.watch('startDate') || '';
  const endDate = form.watch('endDate') || '';
  const startDateObj = toValidDate(startDate);
  const endDateObj = toValidDate(endDate);

  return (
    <div id={id} className={id ? 'scroll-mt-28' : undefined}>
      <DetailSectionHeader title={t('contracts.sections.masterData')} />

      {mode === 'read' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            {readCard(t('contracts.title'), title.trim() !== '' ? title : '-')}
          </div>
          <div>
            {readCard(t('contracts.orderNumber'), orderNumber.trim() !== '' ? orderNumber : '-')}
          </div>
          <div>{readCard(t('contracts.statusLabel'), t(`contracts.status.${status}` as any))}</div>
          <div>
            {readCard(
              t('contracts.startDate'),
              startDate ? DateTime.fromISO(startDate).toFormat('dd.MM.yyyy') : '-'
            )}
          </div>
          <div>
            {readCard(
              t('contracts.endDate'),
              endDate ? DateTime.fromISO(endDate).toFormat('dd.MM.yyyy') : '-'
            )}
          </div>
          <div className="md:col-span-2">
            {readCard(
              t('contracts.customerAssignment'),
              <EntityReferenceValue label={customerName} href={customerHref} />
            )}
          </div>
          <div>
            {readCard(
              t('contracts.weaOssCount'),
              typeof weaOssCount === 'number' ? String(weaOssCount) : '-'
            )}
          </div>
          {canReadObjects ? (
            <div className="md:col-span-3">
              <div className="rounded-xl border border-border bg-card p-4">
                <div className="text-xs font-medium text-muted-foreground mb-3">
                  {t('contracts.objects')}
                </div>

                {aggregatedObjectIds.length === 0 ? (
                  <div className="text-sm text-muted-foreground">
                    {t('contracts.noAggregatedObjects')}
                  </div>
                ) : isLoadingObjects ? (
                  <div className="flex flex-wrap gap-2">
                    {aggregatedObjectIds.map((_, index) => (
                      <Skeleton key={index} className="h-[22px] w-20" />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {(aggregatedObjects || []).map((obj) => (
                      <Badge key={obj.id} variant="secondary">
                        {obj.href ? (
                          <Link
                            to={obj.href}
                            className="text-primary hover:underline underline-offset-2"
                          >
                            {obj.name}
                          </Link>
                        ) : (
                          obj.name
                        )}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
          <div className="md:col-span-3">
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                {t('contracts.contractNote')}
              </div>
              <div className="text-sm text-foreground whitespace-pre-wrap wrap-break-words">
                {note.trim() !== '' ? note : '-'}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel required>{t('contracts.title')}</FormLabel>
                  <FormControl>
                    <Input data-testid="contract-title-input" {...field} />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="orderNumber"
            render={({ field }) => (
              <FormItem>
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel>{t('contracts.orderNumber')}</FormLabel>
                  <FormControl>
                    <Input data-testid="contract-order-number-input" {...field} />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel>{t('contracts.statusLabel')}</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value || 'open'}
                      onValueChange={field.onChange}
                      disabled={!canEditContracts}
                    >
                      <SelectTrigger>
                        <div className="flex items-center">
                          <CircleDot className="size-4 mr-2 text-muted-foreground" />
                          <SelectValue />
                        </div>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="open">{t('contracts.status.open')}</SelectItem>
                        <SelectItem value="closed">{t('contracts.status.closed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel required>{t('contracts.startDate')}</FormLabel>
                  <FormControl>
                    <div data-testid="contract-start-date-picker">
                      <DatePicker
                        date={startDateObj}
                        setDate={(date) => field.onChange(date?.toISOString() || '')}
                        placeholder={t('contracts.chooseStartDate')}
                        defaultMonth={startDateObj}
                        maxDate={endDateObj}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="endDate"
            render={({ field }) => (
              <FormItem>
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel required>{t('contracts.endDate')}</FormLabel>
                  <FormControl>
                    <div data-testid="contract-end-date-picker">
                      <DatePicker
                        date={endDateObj}
                        setDate={(date) => field.onChange(date?.toISOString() || '')}
                        placeholder={t('contracts.chooseEndDate')}
                        defaultMonth={endDateObj}
                        minDate={startDateObj}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="customer"
            render={({ field }) => (
              <FormItem className="h-full md:col-span-2">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2 h-full flex flex-col">
                  <FormLabel required>{t('customers.customer')}</FormLabel>
                  <FormControl>
                    <SearchCombobox
                      value={field.value || undefined}
                      onSelect={field.onChange}
                      collectionKey="customers"
                      searchKey="name"
                      disabled={!canEditContracts || Boolean(disableCustomerSelection)}
                      texts={{
                        selectItemMsg: t('contracts.selectCustomer'),
                        searchPlaceholder: t('contracts.searchCustomer'),
                      }}
                      icon={Building2}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="weaOssCount"
            render={({ field }) => (
              <FormItem>
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel>{t('contracts.weaOssCount')}</FormLabel>
                  <FormControl>
                    <Input
                      data-testid="contract-wea-oss-count-input"
                      type="number"
                      value={field.value ?? ''}
                      onChange={(event) => {
                        const value = event.target.value;
                        field.onChange(value === '' ? undefined : Number(value));
                      }}
                      min={0}
                    />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          {canReadObjects ? (
            <div className="md:col-span-3 h-full">
              <div className="rounded-xl border border-border bg-card p-4 space-y-4 h-full flex flex-col">
                <div className="space-y-2">
                  <FormLabel>{t('contracts.objects')}</FormLabel>
                  <FormField
                    control={form.control}
                    name="objects"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <MultiSearchCombobox
                            value={field.value || []}
                            onSelect={field.onChange}
                            collectionKey="objects"
                            searchKey="name"
                            texts={{
                              selectItemMsg: t('contracts.selectObjects'),
                              searchPlaceholder: t('contracts.searchObject'),
                            }}
                            icon={Ship}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2">
                    {t('contracts.aggregatedObjects')}
                  </div>
                  {aggregatedObjectIds.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      {t('contracts.noAggregatedObjects')}
                    </div>
                  ) : isLoadingObjects ? (
                    <div className="flex flex-wrap gap-2">
                      {aggregatedObjectIds.map((_, index) => (
                        <Skeleton key={index} className="h-[22px] w-20" />
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(aggregatedObjects || []).map((obj) => (
                        <Badge key={obj.id} variant="secondary">
                          {obj.name}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}

          <FormField
            control={form.control}
            name="note"
            render={({ field }) => (
              <FormItem className="md:col-span-3">
                <div className="rounded-xl border border-border bg-card p-4 space-y-2">
                  <FormLabel>{t('contracts.contractNote')}</FormLabel>
                  <FormControl>
                    <Textarea data-testid="contract-note-input" {...field} />
                  </FormControl>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />
        </div>
      )}
    </div>
  );
}

type ContractMetadataSectionsProps = {
  mode: Mode;
  form: UseFormReturn<ContractFormData, any, ContractFormData>;
  predefinedSchema: JsonSchemaType | undefined;
  predefinedZod: z.ZodObject<any>;
  predefinedValues: Record<string, unknown> | undefined;
  predefinedSections: ContractMetadataSection[];
  dynamicSchema: JsonSchemaType | undefined;
  dynamicZod: z.ZodObject<any>;
  dynamicValues: Record<string, unknown> | undefined;
  dynamicSections: ContractMetadataSection[];
  isGmnAdmin: boolean;
  onEditDynamicSchema?: () => void;
  groupKey?: string | null;
};

export function ContractMetadataSections({
  mode,
  form,
  predefinedSchema,
  predefinedZod,
  predefinedValues,
  predefinedSections,
  dynamicSchema,
  dynamicZod,
  dynamicValues,
  dynamicSections,
  isGmnAdmin,
  onEditDynamicSchema,
  groupKey,
}: ContractMetadataSectionsProps) {
  const { t } = useIntl();

  const hasPredefined = Boolean(
    predefinedSchema && Object.keys(predefinedSchema.properties || {}).length > 0
  );
  const hasDynamic = Boolean(
    dynamicSchema && Object.keys(dynamicSchema.properties || {}).length > 0
  );

  const renderSection = (section: ContractMetadataSection, content: ReactNode) => (
    <div key={section.id}>
      <div id={section.id} className="scroll-mt-28">
        <DetailSectionHeader title={section.label} />
      </div>
      {content}
    </div>
  );

  const renderPredefined = () => {
    if (!hasPredefined || !predefinedSchema) return null;
    if (predefinedSections.length === 0) {
      return (
        <div className="text-sm text-muted-foreground">({t('metadata.noContractMetadata')})</div>
      );
    }

    return (
      <div className="space-y-10">
        {predefinedSections.map((section) =>
          renderSection(
            section,
            mode === 'read' ? (
              <MetadataReadView
                metadataSchema={predefinedSchema}
                values={predefinedValues}
                fieldNames={section.fieldNames}
                groupKey={groupKey}
              />
            ) : (
              <MetadataAutoForm
                schema={pickZodObjectFields(predefinedZod, section.fieldNames)}
                form={form}
                path={['metadata']}
                metadataSchema={predefinedSchema}
                groupKey={groupKey}
                disableGrouping={true}
              />
            )
          )
        )}
      </div>
    );
  };

  const renderDynamic = () => {
    if (mode === 'read' && !hasDynamic) return null;
    if (!isGmnAdmin && !hasDynamic) return null;

    return (
      <div>
        <div id="contract-custom-metadata" className="scroll-mt-28">
          <DetailSectionHeader
            title={t('metadata.customMetadata')}
            icon={Database}
            showDivider={false}
            action={
              isGmnAdmin && mode === 'edit' && onEditDynamicSchema ? (
                <Button variant="outline" size="sm" onClick={onEditDynamicSchema} type="button">
                  <Edit3 className="size-3!" />
                  {t('metadata.addCustomMetadata')}
                </Button>
              ) : undefined
            }
          />
        </div>

        {mode === 'read' ? (
          hasDynamic && dynamicSchema ? (
            dynamicSections.length > 0 ? (
              <div className="space-y-10">
                {dynamicSections.map((section) =>
                  renderSection(
                    section,
                    <MetadataReadView
                      metadataSchema={dynamicSchema}
                      values={dynamicValues}
                      fieldNames={section.fieldNames}
                      groupKey={groupKey}
                    />
                  )
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>
            )
          ) : null
        ) : hasDynamic && dynamicSchema ? (
          dynamicSections.length > 0 ? (
            <div className="space-y-10">
              {dynamicSections.map((section) =>
                renderSection(
                  section,
                  <MetadataAutoForm
                    schema={pickZodObjectFields(dynamicZod, section.fieldNames)}
                    form={form}
                    path={['dynamicMetadataValues']}
                    metadataSchema={dynamicSchema}
                    groupKey={groupKey}
                    disableGrouping={true}
                  />
                )
              )}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>
          )
        ) : (
          <div className="text-sm text-muted-foreground">({t('metadata.noMetadata')})</div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-10">
      {renderPredefined()}
      {hasPredefined && hasDynamic && <div className="h-px bg-border my-1" aria-hidden="true" />}
      {renderDynamic()}
    </div>
  );
}
